import { Permissions, PermissionPageKey } from '@utility/constants/permission';
import { CommonUtility } from '@utility/common';

// Menu item type (simplified for utils)
export type MenuItem = {
  key: string;
  link?: string;
  permission?: string;
  accordion?: boolean;
  genericLink?: string;
  routeRoot?: string;
  parentLink?: string;
  menus?: MenuItem[];
  text: string;
  count?: number;
  hidden?: boolean;
  collapseMenus?: boolean;
  phosphorIcon?: React.ReactNode;
};

/**
 * Filter sidebar menu based on permissions and summary view
 */
export function filterSidebarMenu(
  menus: MenuItem[],
  isSummaryView: boolean,
): MenuItem[] {
  if (isSummaryView) {
    return menus
      .filter(d => d.key === PermissionPageKey.SCM)
      .map(d => ({ ...d, accordion: false }));
  }

  // Children are filtered wherever there are children. Gating this on
  // `accordion` used to let Orders and Purchasing draw entries the reader is
  // hidden from, since neither sets the flag.
  return (
    menus
      .map(menu =>
        menu.menus
          ? {
              ...menu,
              menus: menu.menus.filter(
                child =>
                  !child.hidden &&
                  child.permission &&
                  child.permission !== Permissions.hidden,
              ),
            }
          : menu,
      )
      .filter(menu => (menu.menus ? menu.menus.length > 0 : Boolean(menu.link)))
      .filter(menu => menu.permission !== Permissions.hidden)
      /* A collapsed group is one row, opening the first page its reader may see. */
      .map(menu =>
        menu.collapseMenus && menu.menus
          ? { ...menu, link: menu.menus[0].link, menus: undefined }
          : menu,
      )
  );
}

/**
 * Check if redirect is needed in summary view
 */
export function shouldRedirectInSummaryView(
  pathname: string,
  sidebarMenu: MenuItem[],
): boolean {
  // These paths should never redirect
  if (
    pathname?.startsWith('/public/') ||
    pathname?.startsWith('/app/onboarding') ||
    pathname?.startsWith('/shopify/billing')
  ) {
    return false;
  }

  // Root app path doesn't redirect
  if (pathname === '/app/') {
    return false;
  }

  // Check if current path matches any menu item
  for (const { link: parentLink, menus: subMenus } of sidebarMenu) {
    if (pathname === `${parentLink}/`) {
      return false;
    }

    if (CommonUtility.isValidArray(subMenus)) {
      for (const { link: subLink } of subMenus) {
        if (pathname.startsWith(`${subLink}/`)) {
          return false;
        }
      }
    }
  }

  return true;
}

/**
 * Some routes have no entry of their own — an order's detail, clone and
 * refund pages all sit under Orders without being any one status. Those name
 * the entry they belong to in `routeRoot`/`parentLink`, and the section is
 * marked rather than a child, since the reader is on none of them.
 *
 * The longest root wins, so an entry nested under another claims its own
 * routes instead of leaving them to the shallower one.
 */
function getSectionKey(
  sidebarMenu: MenuItem[],
  pathname: string,
): string | undefined {
  let match: { root: string; id: string } | undefined;

  for (const item of sidebarMenu) {
    const root = item.routeRoot ?? item.parentLink;
    // The ids `toEntries` hands to SideNav: a key for a group, a link for a row.
    const id = CommonUtility.isValidArray(item.menus) ? item.key : item.link;

    // A whole segment, so `/app/orders` does not claim `/app/orders-archive`.
    if (!root || !id || !pathname.startsWith(`${root}/`)) {
      continue;
    }

    if (!match || root.length > match.root.length) {
      match = { root, id };
    }
  }

  return match?.id;
}

/**
 * The id of the entry the current route sits on. Links, not keys, for rows:
 * several entries share a permission key, so only the link tells them apart.
 */
export function getActiveKey(
  sidebarMenu: MenuItem[],
  pathname: string,
): string | undefined {
  for (const item of sidebarMenu) {
    for (const entry of item.menus ?? [item]) {
      const isActive = entry.genericLink
        ? pathname.includes(entry.genericLink)
        : `${entry.link}/` === pathname;

      if (isActive && entry.link) {
        return entry.link;
      }
    }
  }

  return getSectionKey(sidebarMenu, pathname);
}

/** The entries the sidebar draws, picked out of the permission tree. */
export type Navigation = {
  /** The list itself, top to bottom. */
  main: MenuItem[];
  /** Pinned above the account menu, next to Settings. */
  footer: MenuItem[];
  /** Inside the account menu. */
  account: MenuItem[];
};

/*
 * The tree in `hooks/sidebar` stays whole: route guards resolve every page's
 * permission from it, including pages the sidebar no longer links to. Only
 * these entries are drawn; Catalog and Orders are already one row each, since
 * `filterSidebarMenu` collapses their pages into the first one the reader may
 * open.
 */
const MAIN: ({ group: string } | { link: string })[] = [
  { link: '/app/dashboard/awaiting-actions' },
  { group: PermissionPageKey.Catalog },
  { group: PermissionPageKey.Orders },
  { link: '/app/scm/storage-provider' },
  { link: '/app/shipments' },
];
const FOOTER = ['/app/dashboard/logs'];
const ACCOUNT = [
  '/app/data-studio/analytics',
  '/app/crm',
  '/app/data-studio/inventory-report',
  '/app/data-studio/manifest-reports',
];

/** EN-4134: a V2 account has no data behind the footer and account pages (all legacy reports). */
export function buildNavigation(
  sidebarMenu: MenuItem[],
  v2 = false,
): Navigation {
  const pages = sidebarMenu.flatMap(item => item.menus ?? []);
  const byLink = (link: string) => pages.find(page => page.link === link);
  const pick = (links: string[]) => links.flatMap(link => byLink(link) ?? []);

  return {
    main: MAIN.flatMap(entry =>
      'group' in entry
        ? (sidebarMenu.find(
            item => item.key === entry.group && !item.menus && item.link,
          ) ?? [])
        : (byLink(entry.link) ?? []),
    ),
    footer: v2 ? [] : pick(FOOTER),
    account: v2 ? [] : pick(ACCOUNT),
  };
}
